import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { FontFamily } from "../theme/fontFamily";
import { ThemeColor } from "../theme/color";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "520px",
  maxHeight: "88vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.16)",
  borderRadius: "20px",
  p: 4,
  outline: "none",
  border: "none",
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
};

const STEP = {
  CONFIRM: "confirm",
  DELETING: "deleting",
  BLOCKED: "blocked",
  SUCCESS: "success",
  ERROR: "error",
};

/**
 * Delete confirmation that also explains a refused delete.
 *
 * The delete APIs answer 409 with
 *   { message, blockedBy: [{ section, count }] }
 * when the record's id is still assigned in another section. This modal shows
 * that breakdown instead of a generic failure toast, so the user can see exactly
 * what to reassign first.
 *
 * Props:
 *   open, onClose
 *   entityLabel   "Organization" | "University"
 *   entityName    the record's name, shown in the prompt
 *   cascadeItems  string[] — what else gets removed on success
 *   onDelete      async () => axios response; must NOT swallow the error
 *   onDeleted     called once after a successful delete (refresh the list)
 */
const DeleteWithDependencyModal = ({
  open,
  onClose,
  entityLabel = "record",
  entityName = "",
  cascadeItems = [],
  onDelete,
  onDeleted,
}) => {
  const [step, setStep] = useState(STEP.CONFIRM);
  const [blockers, setBlockers] = useState([]);
  const [serverMessage, setServerMessage] = useState("");
  const [deletedSummary, setDeletedSummary] = useState(null);

  // Reset every time the modal is reopened, otherwise it would show the previous
  // record's outcome for a moment.
  useEffect(() => {
    if (open) {
      setStep(STEP.CONFIRM);
      setBlockers([]);
      setServerMessage("");
      setDeletedSummary(null);
    }
  }, [open]);

  const handleDelete = async () => {
    setStep(STEP.DELETING);

    try {
      const res = await onDelete();
      setServerMessage(res?.data?.message || "");
      setDeletedSummary(res?.data?.deleted || null);
      setStep(STEP.SUCCESS);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 409) {
        setBlockers(Array.isArray(data?.blockedBy) ? data.blockedBy : []);
        setServerMessage(data?.message || "");
        setStep(STEP.BLOCKED);
        return;
      }

      setServerMessage(
        data?.message || error?.message || "Something went wrong. Please try again."
      );
      setStep(STEP.ERROR);
    }
  };

  /** Closing after a successful delete has to refresh the caller's list. */
  const handleClose = () => {
    const wasDeleted = step === STEP.SUCCESS;
    onClose();
    if (wasDeleted && typeof onDeleted === "function") onDeleted();
  };

  const heading = {
    [STEP.CONFIRM]: `Delete this ${entityLabel.toLowerCase()}?`,
    [STEP.DELETING]: "Deleting…",
    [STEP.BLOCKED]: `This ${entityLabel.toLowerCase()} cannot be deleted`,
    [STEP.SUCCESS]: `${entityLabel} deleted`,
    [STEP.ERROR]: "Delete failed",
  }[step];

  const icon = {
    [STEP.CONFIRM]: (
      <WarningAmberRoundedIcon sx={{ fontSize: 46, color: "#F59E0B" }} />
    ),
    [STEP.DELETING]: <CircularProgress size={40} />,
    [STEP.BLOCKED]: <BlockRoundedIcon sx={{ fontSize: 46, color: "#DC2626" }} />,
    [STEP.SUCCESS]: (
      <CheckCircleRoundedIcon sx={{ fontSize: 46, color: "#16A34A" }} />
    ),
    [STEP.ERROR]: (
      <ErrorOutlineRoundedIcon sx={{ fontSize: 46, color: "#DC2626" }} />
    ),
  }[step];

  const panel = (background, border) => ({
    background,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    p: 2,
    fontFamily: FontFamily.Poppins,
    fontSize: "0.9rem",
    lineHeight: 1.6,
  });

  return (
    <Modal open={Boolean(open)} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          {icon}
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={600}
            fontSize="1.25rem"
            color={ThemeColor.BLACK}
            align="center"
          >
            {heading}
          </Typography>
          {entityName && (
            <Typography
              fontFamily={FontFamily.Poppins}
              fontWeight={600}
              fontSize="0.95rem"
              color={ThemeColor.NAVYBLUE}
              align="center"
            >
              {entityName}
            </Typography>
          )}
        </Box>

        {/* ---- confirm ---- */}
        {step === STEP.CONFIRM && (
          <>
            <Typography sx={panel("rgba(220, 38, 38, 0.05)", "rgba(220, 38, 38, 0.16)")}>
              This cannot be undone. The {entityLabel.toLowerCase()} will only be
              deleted if its id is not assigned anywhere else — if it is, you'll
              see exactly what is blocking it.
            </Typography>

            {cascadeItems.length > 0 && (
              <Box sx={panel("rgba(19, 85, 155, 0.05)", "rgba(19, 85, 155, 0.14)")}>
                <Typography
                  fontFamily={FontFamily.Poppins}
                  fontWeight={600}
                  fontSize="0.85rem"
                  sx={{ mb: 1 }}
                >
                  These will be permanently deleted too:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {cascadeItems.map((item) => (
                    <Box
                      component="li"
                      key={item}
                      sx={{ fontSize: "0.85rem", mb: 0.4 }}
                    >
                      {item}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* ---- blocked: the reason ---- */}
        {step === STEP.BLOCKED && (
          <>
            <Box sx={panel("rgba(220, 38, 38, 0.05)", "rgba(220, 38, 38, 0.16)")}>
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={600}
                fontSize="0.85rem"
                sx={{ mb: 1 }}
              >
                Its id is still assigned to:
              </Typography>

              {blockers.length > 0 ? (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {blockers.map((entry) => (
                    <Box
                      component="li"
                      key={entry.section}
                      sx={{ fontSize: "0.9rem", mb: 0.5 }}
                    >
                      <strong>{entry.count}</strong> {entry.section}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography fontSize="0.9rem">
                  {serverMessage || "It is referenced by other records."}
                </Typography>
              )}
            </Box>

            <Typography
              fontFamily={FontFamily.Poppins}
              fontSize="0.85rem"
              color={ThemeColor.GREY}
              align="center"
            >
              Reassign or remove those records first, then try again.
            </Typography>
          </>
        )}

        {/* ---- success ---- */}
        {step === STEP.SUCCESS && (
          <Box sx={panel("rgba(22, 163, 74, 0.05)", "rgba(22, 163, 74, 0.16)")}>
            <Typography fontSize="0.9rem" sx={{ mb: deletedSummary ? 1 : 0 }}>
              {serverMessage || "Deleted successfully."}
            </Typography>

            {deletedSummary && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {Object.entries(deletedSummary)
                  // The name is already in the heading, and zero counts are noise.
                  .filter(([key, value]) => typeof value === "number" && value > 0)
                  .map(([key, value]) => (
                    <Box component="li" key={key} sx={{ fontSize: "0.85rem" }}>
                      {value} {key.replace(/([A-Z])/g, " $1").toLowerCase()}
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {/* ---- unexpected failure ---- */}
        {step === STEP.ERROR && (
          <Typography sx={panel("rgba(220, 38, 38, 0.05)", "rgba(220, 38, 38, 0.16)")}>
            {serverMessage}
          </Typography>
        )}

        {/* ---- actions ---- */}
        <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
          {step === STEP.CONFIRM && (
            <>
              <Button
                variant="contained"
                fullWidth
                onClick={handleDelete}
                sx={{
                  bgcolor: "rgba(236, 50, 57, 1)",
                  color: "white",
                  borderRadius: "12px",
                  py: 1.25,
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: FontFamily.Poppins,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "rgba(236, 50, 57, 0.9)", boxShadow: "none" },
                }}
              >
                Yes, delete
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                sx={{
                  borderRadius: "12px",
                  py: 1.25,
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: FontFamily.Poppins,
                }}
              >
                Cancel
              </Button>
            </>
          )}

          {step !== STEP.CONFIRM && step !== STEP.DELETING && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{
                bgcolor: "rgba(19, 85, 155, 1)",
                color: "white",
                borderRadius: "12px",
                py: 1.25,
                textTransform: "none",
                fontWeight: 600,
                fontFamily: FontFamily.Poppins,
                boxShadow: "none",
                "&:hover": { bgcolor: "rgba(19, 85, 155, 0.9)", boxShadow: "none" },
              }}
            >
              {step === STEP.BLOCKED ? "Understood" : "Close"}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteWithDependencyModal;
